import React, { FC, ReactElement, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  Row,
  Col,
  Layout,
  Affix,
  Tabs,
  Divider,
  Button,
  Space,
  Typography,
  Rate,
} from "antd";
import {
  ShoppingCartOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import SockJS from "sockjs-client";
import { CompatClient, Stomp } from "@stomp/stompjs";

import {
  selectIsPerfumeLoaded,
  selectIsPerfumeLoading,
  selectPerfume,
  selectPerfumeError,
  selectPerfumeErrorMessage,
  selectReviews,
} from "../../redux-toolkit/perfume/perfume-selector";
import { selectReviewErrors } from "../../redux-toolkit/user/user-selector";
import {
  fetchPerfume,
  fetchReviewsByPerfumeId,
} from "../../redux-toolkit/perfume/perfume-thunks";
import { resetInputForm } from "../../redux-toolkit/user/user-slice";
import { WEBSOCKET_URL } from "../../constants/urlConstants";
import {
  resetPerfumeState,
  setReview,
} from "../../redux-toolkit/perfume/perfume-slice";
import { addReviewToPerfume } from "../../redux-toolkit/user/user-thunks";
import { useCart } from "../../hooks/useCart";

import Spinner from "../../components/Spinner/Spinner";
import ErrorMessage from "./ErrorMessage/ErrorMessage";
import ProductReviews from "./ProductReviews/ProductReviews";

import "./Product.css";

let stompClient: CompatClient | null = null;
const { TabPane } = Tabs;

const Product: FC = (): ReactElement => {
  const dispatch = useDispatch();
  const params = useParams<{ id: string }>();

  const perfume = useSelector(selectPerfume);
  const reviews = useSelector(selectReviews);
  const isPerfumeLoading = useSelector(selectIsPerfumeLoading);
  const isPerfumeLoaded = useSelector(selectIsPerfumeLoaded);
  const isPerfumeError = useSelector(selectPerfumeError);
  const errorMessage = useSelector(selectPerfumeErrorMessage);
  const reviewErrors = useSelector(selectReviewErrors);

  const [affixTop] = useState<number>(80);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const { addToCart } = useCart(perfume);

  useEffect(() => {
    if (params.id) {
      dispatch(fetchPerfume(params.id));
      dispatch(resetInputForm());
      window.scrollTo(0, 0);
    }

    try {
      stompClient = Stomp.over(() => new SockJS(WEBSOCKET_URL));
      stompClient.connect({}, () => {
        if (params.id) {
          stompClient?.subscribe(`/topic/reviews/${params.id}`, (response: any) => {
            dispatch(setReview(JSON.parse(response.body)));
          });
        }
      });
    } catch (e) {}

    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);

    return () => {
      stompClient?.disconnect();
      dispatch(resetPerfumeState());
      window.removeEventListener("resize", handleResize);
    };
  }, [dispatch, params.id]);

  useEffect(() => {
    if (isPerfumeLoaded && params.id) {
      dispatch(fetchReviewsByPerfumeId(params.id));
    }
  }, [isPerfumeLoaded, dispatch, params.id]);

  if (isPerfumeLoading) return <Spinner />;
  if (isPerfumeError) return <ErrorMessage errorMessage={errorMessage} />;

  return (
    <Layout className="product-page-layout">
      <Layout.Content className="product-content">
        <Row gutter={[24, 24]} justify="center">
          {/* ===== LEFT CONTENT ===== */}
          <Col xs={24} lg={16}>
            {/* ✅ Product Summary Section */}
            {perfume && (
              <div className="product-summary">
                <img
                  src={perfume?.filename || "/no-image.png"}
                  alt={perfume?.perfumeTitle}
                  className="product-image-large"
                />

                <Typography.Title
                  level={3}
                  className="product-title-large"
                >
                  {perfume?.perfumeTitle}
                </Typography.Title>

                <Typography.Text className="product-brand">
                  {perfume?.brand}
                </Typography.Text>

                <div className="product-rating">
                  <Rate
                    allowHalf
                    disabled
                    defaultValue={perfume?.perfumeRating || 0}
                    style={{ color: "#fadb14" }}
                  />
                  <span className="reviews-text">
                    {perfume?.perfumeRating || 0} ★ • {reviews?.length || 0} reviews
                  </span>
                </div>

                <div className="product-price-section">
                  <Typography.Text className="product-price">
                    ₹{perfume?.price}
                  </Typography.Text>
                  {perfume?.actualPrice && (
                    <Typography.Text className="product-actual-price">
                      ₹{perfume.actualPrice}
                    </Typography.Text>
                  )}
                  {perfume?.discount && perfume.discount > 0 && (
                    <Typography.Text className="product-discount">
                      ({perfume.discount}% OFF)
                    </Typography.Text>
                  )}
                </div>
                  {perfume?.discount && perfume.discount > 0 && (
                     <Typography.Text className="min-quantity">
                      Min Qty : {perfume.minQuantity}
                       </Typography.Text>
                   )}

                <div className="product-delivery">
                  🚚 {" "}
                  {perfume?.deliveryTimeline || "7 Days"}
                </div>

                {/* ✅ Add to Cart / Buy Now Buttons (Visible in Web view too) */}
                {!isMobile && (
                  <Space size="middle" style={{ marginTop: 20 }}>
                    <Button
                      type="primary"
                      icon={<ShoppingCartOutlined />}
                      style={{
                        background: "#ff9f00",
                        border: "none",
                        padding: "0 32px",
                      }}
                      onClick={() => {
                        addToCart();
                        window.location.href = "/cart";
                      }}
                    >
                      Add to Cart
                    </Button>
                    <Button
                      type="primary"
                      icon={<ThunderboltOutlined />}
                      style={{
                        background: "#fb641b",
                        border: "none",
                        padding: "0 32px",
                      }}
                      onClick={() => {
                        addToCart();
                        window.location.href = "/cart";
                      }}
                    >
                      Buy Now
                    </Button>
                  </Space>
                )}
              </div>
            )}

            {/* ✅ Tabs Section */}
            <Tabs defaultActiveKey="details" type="line" size="large">
              <TabPane tab="Details" key="details">
                <div style={{ padding: 8 }}>
                  <h3>Product details</h3>
                  <p style={{ whiteSpace: "pre-wrap" }}>
                    {perfume?.description || "No description available."}
                  </p>
                  <Divider />
                  <Row gutter={[16, 16]}>
                    <Col xs={12}>
                      <b>Brand:</b> {perfume?.brand || "-"}
                    </Col>
                    <Col xs={12}>
                      <b>Category:</b> {perfume?.category || "-"}
                    </Col>
                    <Col xs={12}>
                      <b>Sub category:</b> {perfume?.subCategory || "-"}
                    </Col>
                    <Col xs={12}>
                      <b>Unit:</b> {perfume?.unit || "-"}
                    </Col>
                    <Col xs={12}>
                      <b>Seller:</b> {perfume?.sellerName || "-"}
                    </Col>
                    <Col xs={12}>
                      <b>Delivery:</b>{" "}
                      {perfume?.deliveryTimeline
                        ? `${perfume.deliveryTimeline}`
                        : "Within 7 Days"}
                    </Col>
                  </Row>
                </div>
              </TabPane>

              <TabPane tab={`Reviews (${reviews?.length || 0})`} key="reviews">
                <ProductReviews
                  reviews={reviews}
                  reviewErrors={reviewErrors}
                  addReview={async (d) => {
                    await dispatch(addReviewToPerfume({ perfumeId: params.id!, ...d }));
                  }}
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Layout.Content>

      {/* ✅ Mobile Bottom Sticky (with Buy Now visible again) */}
      {isMobile && (
        <div className="mobile-bottom-card">
          <div className="mobile-bottom-content">
            <div className="mobile-buttons">
              <Button
                className="mobile-add-to-cart"
                icon={<ShoppingCartOutlined />}
                onClick={() => {
                  addToCart();
                  window.location.href = "/cart";
                }}
              >
                Add to Cart
              </Button>
              <Button
                className="mobile-buy-now"
                icon={<ThunderboltOutlined />}
                onClick={() => {
                  addToCart();
                  window.location.href = "/cart";
                }}
              >
                Buy Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Product;
